import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import WSTextField from "../../Components/TextBox/WSTextBox";
import WSTextAreaField from "../../Components/WSTextArea/WSTextArea";
import WSButton from "../../Components/Button/WSButton";
import ConfirmationPopup from "../../Components/Confirmation/Confirmation";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { callAPI, Method, updateMainHeader } from "../../commonFunctions/CommonFunctions";
import { getPermissions } from "../../commonFunctions/PermissionManger";
import PermissionDenied from "../../commonFunctions/PermissionDenied";
import http from "../../commonFunctions/httpClient";
import { PermissionMatrix } from "./PermissionMatrix";
import { emptyMatrix, fromApiPermissions, PermissionMatrixValue, ROLE_API, toApiPermissions } from "./rolePermissionConfig";

const NEW_ROLE = "new";

const primaryButtonSx = {
  borderRadius: 8,
  width: "8rem",
  border: "1px solid #098963",
  color: "#FFFFFF",
  backgroundColor: "#098963",
  "&:hover": { backgroundColor: "#066e4f" },
};
const panelSx = { p: 2, borderRadius: 2, border: "1px solid lightgrey" };

/**
 * Roles & Permissions on one page: role list on the left; the selected role's
 * name, description and page × action permissions on the right.
 */
export const RolesAndPermissions: React.FC = () => {
  const { showMessage } = useSnackbar();
  const { view, add, edit, remove } = getPermissions("roles");
  const { control, handleSubmit, reset, formState } = useForm({ defaultValues: { name: "", description: "" } });

  const [roles, setRoles] = useState<any[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null); // role id, NEW_ROLE, or null
  const [matrix, setMatrix] = useState<PermissionMatrixValue>(emptyMatrix);
  const [matrixDirty, setMatrixDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isNew = selectedId === NEW_ROLE;
  const canSave = isNew ? add : edit;
  const dirty = formState.isDirty || matrixDirty;

  useEffect(() => {
    updateMainHeader("Roles & Permissions");
    breadCrumbsStore.updateBreadCrumbs([{ "Roles & Permissions": "/role/list" }]);
  }, []);

  const loadRoles = useCallback(async () => {
    try {
      setRolesLoading(true);
      // Roles are a short list; fetch them in one page.
      const response: any = await http.post(ROLE_API.search, {
        page: 1,
        pageSize: 500,
        filter: [],
        sort: [{ field: "Name", direction: "asc" }],
      });
      setRoles(response?.data || []);
    } catch (e) {
      showMessage("Could not load roles", "error");
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const confirmDiscard = () => !dirty || window.confirm("You have unsaved changes. Discard them?");

  const openRole = async (id: string) => {
    if (id === selectedId || !confirmDiscard()) return;
    setSelectedId(id);
    setMatrixDirty(false);
    if (id === NEW_ROLE) {
      reset({ name: "", description: "" });
      setMatrix(emptyMatrix());
      return;
    }
    try {
      const response: any = await callAPI(ROLE_API.base, Method.GET, null, id);
      const role = response?.data;
      if (!role) {
        showMessage(response?.message || "Role not found", "error");
        return;
      }
      reset({ name: role.name || "", description: role.description || "" });
      setMatrix(fromApiPermissions(role.permissions));
    } catch (e) {
      showMessage("Could not load role", "error");
    }
  };

  const handleMatrixChange = (value: PermissionMatrixValue) => {
    setMatrix(value);
    setMatrixDirty(true);
  };

  const handleSave = async (data: { name: string; description: string }) => {
    const request = {
      name: (data.name || "").trim(),
      description: (data.description || "").trim(),
      permissions: toApiPermissions(matrix),
    };
    try {
      setSaving(true);
      const response: any = isNew
        ? await callAPI(ROLE_API.base, Method.POST, request)
        : await callAPI(ROLE_API.base, Method.PUT, request, selectedId);
      if (response?.success === false) {
        showMessage(response?.message || "Could not save role", "error");
        return;
      }
      showMessage(response?.message || `Role ${isNew ? "created" : "updated"} successfully`, "success");
      reset({ name: request.name, description: request.description }); // saved values become the clean baseline
      setMatrixDirty(false);
      const newId = response?.data?.id ?? response?.id;
      if (isNew && newId) setSelectedId(String(newId));
      loadRoles();
    } catch (e: any) {
      showMessage(e?.error || "Could not save role", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleted = (response: any) => {
    if (response?.success === false) {
      showMessage(response?.message || "Could not delete role", "error");
      return;
    }
    showMessage(response?.message || "Role deleted successfully", "success");
    setSelectedId(null);
    setMatrixDirty(false);
    reset({ name: "", description: "" });
    loadRoles();
  };

  const visibleRoles = useMemo(() => {
    const term = roleSearch.trim().toLowerCase();
    return term ? roles.filter((r) => r?.name?.toLowerCase().includes(term)) : roles;
  }, [roles, roleSearch]);

  if (!view) return <PermissionDenied />;

  return (
    <Grid p={3}>
      <Grid container spacing={2} alignItems="flex-start">
        {/* Role list */}
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={panelSx}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>Roles</Typography>
              {add && (
                <Tooltip title="Add role">
                  <IconButton size="small" onClick={() => openRole(NEW_ROLE)} aria-label="Add role" sx={{ color: "#098963" }}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by role name"
              value={roleSearch}
              onChange={(e) => setRoleSearch(e.target.value)}
              inputProps={{ "aria-label": "Search by role name" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <List dense sx={{ mt: 1, maxHeight: "60vh", overflowY: "auto" }}>
              {isNew && (
                <ListItemButton selected>
                  <ListItemText primary="New role" primaryTypographyProps={{ fontSize: "0.85rem", fontStyle: "italic" }} />
                </ListItemButton>
              )}
              {visibleRoles.map((role) => (
                <ListItemButton key={role.id} selected={String(role.id) === selectedId} onClick={() => openRole(String(role.id))}>
                  <ListItemText
                    primary={role.name}
                    secondary={role.description}
                    primaryTypographyProps={{ fontSize: "0.85rem" }}
                    secondaryTypographyProps={{ fontSize: "0.7rem", noWrap: true }}
                  />
                </ListItemButton>
              ))}
              {!rolesLoading && visibleRoles.length === 0 && (
                <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", p: 1 }}>
                  {roles.length === 0 ? "No roles yet." : "No matching roles."}
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Selected role: details + permissions */}
        <Grid item xs={12} md={9}>
          <Paper elevation={0} sx={panelSx}>
            {selectedId === null ? (
              <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", textAlign: "center", py: 6 }}>
                Select a role to view or change its permissions{add ? ", or add a new role." : "."}
              </Typography>
            ) : (
              <form onSubmit={handleSubmit(handleSave)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <WSTextField
                      label="Role Name *"
                      id="name"
                      name="name"
                      control={control}
                      maxLength={50}
                      disabled={!canSave}
                      rules={{ required: true, validate: (v: string) => !!v?.trim() || "Role name is required" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <WSTextAreaField label="Description" id="description" name="description" control={control}
                      rows={1} maxLength={250} disabled={!canSave} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 1 }}>
                      Granting Add, Edit, Delete or Export also grants View for that page.
                    </Typography>
                    <PermissionMatrix value={matrix} onChange={handleMatrixChange} disabled={!canSave} />
                  </Grid>
                  <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
                    {!isNew && remove && (
                      <WSButton id="deleteRole" type="button" label="Delete" size="medium" variant="outlined" color="primary"
                        startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)}
                        sx={{ borderRadius: 8, width: "8rem", border: "1px solid #d32f2f", color: "#d32f2f", backgroundColor: "#FFFFFF" }} />
                    )}
                    {canSave && (
                      <WSButton id="saveRole" type="submit" label="Save" size="medium" variant="outlined" color="primary"
                        loading={saving} disabled={!dirty} sx={primaryButtonSx} />
                    )}
                  </Grid>
                </Grid>
              </form>
            )}
          </Paper>
        </Grid>
      </Grid>

      {selectedId && !isNew && (
        <ConfirmationPopup
          apiUrl={ROLE_API.base}
          type="DELETE"
          id={selectedId}
          confirmMessage="Delete this role? Users assigned to it will lose these permissions."
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={handleDeleted}
        />
      )}
    </Grid>
  );
};
